import { useState } from 'react';
import { useFormik } from 'formik';

import useToast from '../../../hooks/useToast';
import { workReportLineStatus } from '../../../utils/dataStatus';
import {
  approveAdapter,
  approveAllAdapter,
  approveLocationProgramPointApdater,
  editLocationProgramPointAdapter,
  editQuantityAdapter,
  getLocationProgramPointAdapter,
  getPerfilEjecuta,
  postLocationProgramPointAdapter,
  rectifyLocationProgramPointAdapter,
  rejectLocationProgramPointAdapter,
  rejectLocationWithoutProgramPointAdapter
} from '../../../services/programPointsService';

const locationsInit = [
  {
    pozoId: 1,
    pozoNombre: null,
    nombreEquipo: null,
    eventoSigla: null,
    habilitaVentana: false,
    totalValue: 0,
    totalValueFijado: 0,
    cantidad: 0,
    programPoints: [
      {
        quantity: null,
        points: null,
        quantityFijados: null,
        pointsFijados: null,
        idPartePuntoPrograma: null,
        idParteDiario: null,
        cost: null,
        code: null,
        subCode: null,
        detail: null,
        clasification: null,
        idOperativo: null
      }
    ]
  }
];

export const defaultInitialValues = {
  locations: locationsInit,
  locationsCopy: locationsInit,
  locationTotalValue: 0,
  locationTotalValueFijado: 0
};

const useLocationProgramPoints = (
  { line, initialValues = defaultInitialValues, validationSchema } = {
    line: undefined,
    initialValues: defaultInitialValues,
    validationSchema: undefined
  }
) => {
  const { setNewToast } = useToast();
  const [loading, setLoading] = useState(false);

  const { values, setFieldValue, resetForm, setFieldTouched, touched, errors } = useFormik({
    initialValues,
    validationSchema
  });

  const calculateTotalValue = (locations) => {
    const locationTotalValue = parseFloat(
      locations.reduce((acc, loc) => Number(acc) + Number(loc.totalValue), 0).toFixed(3)
    );
    const locationTotalValueFijado = parseFloat(
      locations.reduce((acc, loc) => Number(acc) + Number(loc.totalValueFijado), 0).toFixed(3)
    );

    return {
      locationTotalValue,
      locationTotalValueFijado
    };
  };

  const calcularTotales = (locations, fijar = false) => {
    const locationsAdapted = locations.map((location) => {
      const totalValue = location.programPoints.reduce(
        (acc, curr) => acc + Number(curr.quantity),
        0
      );
      const totalValueFijado = fijar
        ? location.programPoints.reduce((acc, curr) => acc + Number(curr.quantityFijados), 0)
        : totalValue;

      return {
        ...location,
        totalValue,
        totalValueFijado: totalValueFijado || totalValue
      };
    });

    const { locationTotalValue, locationTotalValueFijado } = calculateTotalValue(locationsAdapted);

    return {
      locations: locationsAdapted,
      locationTotalValue,
      locationTotalValueFijado
    };
  };

  const getProgramPointsData = async (perfilEjecutaParam, saveInitialCopy = false) => {
    try {
      setLoading(true);

      const esFijado = line.estadoEnum === workReportLineStatus.fijar;

      const perfilEjecuta = perfilEjecutaParam || getPerfilEjecuta();

      const res = await getLocationProgramPointAdapter(line, perfilEjecuta);

      const { locations, locationTotalValue, locationTotalValueFijado } = calcularTotales(
        res,
        esFijado
      );

      setFieldValue(`locationTotalValue`, locationTotalValue);
      setFieldValue(`locationTotalValueFijado`, locationTotalValueFijado);
      setFieldValue(`locations`, locations);
      if (saveInitialCopy) {
        setFieldValue(`locationsCopy`, locations);
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      setNewToast({
        type: 'ERROR',
        message: error?.data?.message || 'No pudimos procesar la solicitud, intente nuevamente.'
      });
    }
  };

  const postLocationProgramPoints = async (locations) => {
    try {
      setLoading(true);

      const result = await postLocationProgramPointAdapter(locations, line);

      setNewToast({
        type: 'SUCCESS',
        message: result.mensaje || 'Partes diarios notificados con éxito.'
      });
      setLoading(false);
    } catch (error) {
      setLoading(false);
      setNewToast({
        type: 'ERROR',
        message: error?.data?.message || 'No pudimos procesar la solicitud, intente nuevamente.'
      });
    }
  };

  const rectifyLocationProgramPoints = async (cantidad, locations, comentario) => {
    try {
      setLoading(true);
      const perfilEjecuta = getPerfilEjecuta();

      const result = await rectifyLocationProgramPointAdapter(
        {
          ...line,
          comentarios: comentario || line?.comentarios || '',
          revisionExperta: line?.revisionExperta ?? null,
          requiereConformidad: line?.requiereConformidad ?? null,
          requiereAdjuntar: line?.requiereAdjuntar ?? null
        },
        cantidad,
        locations,
        perfilEjecuta
      );

      setNewToast({
        type: 'SUCCESS',
        message: result.mensaje || 'Partes diario rectificados con éxito.'
      });
      setLoading(false);
    } catch (error) {
      setLoading(false);
      setNewToast({
        type: 'ERROR',
        message: error?.message || 'No pudimos procesar la solicitud, intente nuevamente.'
      });
    }
  };

  const approveLocationProgramPoints = async (locations, lineData = line) => {
    try {
      setLoading(true);
      const cantidadTotal = calculateTotalValue(locations);
      const result = await approveLocationProgramPointApdater(
        lineData,
        cantidadTotal?.locationTotalValue || 0,
        locations
      );

      if (result?.statusCode === 400) {
        setNewToast({
          type: 'ERROR',
          message: result.mensaje || result.message
        });
      } else {
        setNewToast({
          type: 'SUCCESS',
          message: result.mensaje || result.message
        });
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      setNewToast({
        type: 'ERROR',
        message: error?.data?.message || 'No pudimos procesar la solicitud, intente nuevamente.'
      });
    }
  };

  const approveAll = async (ids) => {
    try {
      setLoading(true);
      const result = await approveAllAdapter(ids);

      if (result?.statusCode === 400) {
        setNewToast({
          type: 'ERROR',
          message: result.mensaje || result.message
        });
      } else {
        setNewToast({
          type: 'SUCCESS',
          message: result.mensaje || result.message
        });
      }

      return result;
    } catch (error) {
      setNewToast({
        type: 'ERROR',
        message: error.message
      });
      setLoading(false);
    }

    return null;
  };

  const approve = async (idParteDiario) => {
    try {
      setLoading(true);
      const result = await approveAdapter(idParteDiario);

      setNewToast({
        type: 'SUCCESS',
        message: result.mensaje || result.message
      });
    } catch (error) {
      setLoading(false);
    }
  };

  const rejectWithProgramPoints = async ({
    cantidad,
    comentario,
    requiereConformidad,
    requiereAdjuntar,
    locations
  }) => {
    try {
      setLoading(true);
      const perfilEjecuta = getPerfilEjecuta();
      const result = await rejectLocationProgramPointAdapter(
        line,
        cantidad,
        comentario,
        locations,
        requiereConformidad,
        requiereAdjuntar,
        perfilEjecuta
      );

      setNewToast({
        type: 'SUCCESS',
        message: result.mensaje || 'Partes diario devueltos con éxito.'
      });
      setLoading(false);
    } catch (error) {
      setLoading(false);
      setNewToast({
        type: 'ERROR',
        message: error?.data?.message || 'No pudimos procesar la solicitud, intente nuevamente.'
      });
    }
  };

  const rejectQuantity = async ({
    cantidad,
    comentario,
    requiereConformidad,
    requiereAdjuntar
  }) => {
    try {
      setLoading(true);
      const perfilEjecuta = getPerfilEjecuta();
      const result = await rejectLocationWithoutProgramPointAdapter(
        line,
        cantidad,
        comentario,
        requiereConformidad,
        requiereAdjuntar,
        perfilEjecuta
      );

      setNewToast({
        type: 'SUCCESS',
        message: result.mensaje || 'Partes diario devueltos con éxito.'
      });
      setLoading(false);
    } catch (error) {
      setLoading(false);
      setNewToast({
        type: 'ERROR',
        message: error?.data?.message || 'No pudimos procesar la solicitud, intente nuevamente.'
      });
    }
  };

  const editWithProgramPoints = async ({ cantidad, comentario, locations }) => {
    try {
      setLoading(true);
      const perfilEjecuta = getPerfilEjecuta();
      const result = await editLocationProgramPointAdapter(
        line,
        cantidad,
        comentario,
        locations,
        perfilEjecuta
      );

      setNewToast({
        type: 'SUCCESS',
        message: result.mensaje || 'Partes diario editados con éxito.'
      });
      setLoading(false);

      return result;
    } catch (error) {
      setLoading(false);
      setNewToast({
        type: 'ERROR',
        message: error?.data?.message || 'No pudimos procesar la solicitud, intente nuevamente.'
      });
    }

    return null;
  };

  const editQuantity = async ({ cantidad, comentario }) => {
    try {
      setLoading(true);
      const perfilEjecuta = getPerfilEjecuta();
      const result = await editQuantityAdapter(line, cantidad, comentario, perfilEjecuta);

      setNewToast({
        type: 'SUCCESS',
        message: result.mensaje || 'Partes diario editados con éxito.'
      });
      setLoading(false);

      return result;
    } catch (error) {
      setLoading(false);
      setNewToast({
        type: 'ERROR',
        message: error?.data?.message || 'No pudimos procesar la solicitud, intente nuevamente.'
      });
    }

    return null;
  };

  return {
    calcularTotales,
    calculateTotalValue,
    getProgramPointsData,
    postLocationProgramPoints,
    rectifyLocationProgramPoints,
    approveLocationProgramPoints,
    rejectWithProgramPoints,
    editWithProgramPoints,
    editQuantity,
    rejectQuantity,
    approve,
    approveAll,
    loading,
    setLoading,
    values,
    resetForm,
    setFieldValue,
    setFieldTouched,
    touched,
    errors
  };
};

export default useLocationProgramPoints;
